/**
 * Direnç bandı yükü — saf mantık.
 *
 * Bant, ağırlık takılan bir ekipman değil: elindeki bantların kendisi ve
 * kombinasyonları neyse yük o. 15 ve 30 kg'lık iki bandın varsa
 * kurabileceğin dirençler 15, 30 ve 45 — arada bir şey yok.
 *
 * Bu yüzden bantta yüzdeyle ilerlemek anlamsız. Motor normalde alt vücutta
 * %5, üstte %2.5 artırıyor; bantta bu "15 → 15.4 kg" gibi kuramayacağın bir
 * sayı üretir. Basamak listesi bunun yerine geçiyor.
 *
 * DÜRÜSTLÜK NOTU: bandın üstündeki kg, tam gerdiğindeki TEPE direnç. Bant
 * gerildikçe sertleşiyor, yani yük hareket boyunca sabit değil — dambıldaki
 * gibi kesin bir sayı değil bu. Yine de "hangi bantla yaptım" bilgisi
 * ilerlemeyi takip etmeye fazlasıyla yetiyor; asıl mesele o.
 */

/** Bir kullanıcının kurabileceği dirençler, küçükten büyüğe. */
export function bantBasamaklari(bandsKg: readonly number[], kombinasyon = true): number[] {
  const temiz = [...new Set(bandsKg.filter((x) => Number.isFinite(x) && x > 0))]
  if (temiz.length === 0) return []
  if (!kombinasyon) return temiz.sort((a, b) => a - b)

  // Boş olmayan tüm alt kümelerin toplamı. Bant sayısı elde tutulabilecek
  // kadar az (2–5), 2^n burada bir sorun değil.
  const toplamlar = new Set<number>()
  for (let maske = 1; maske < 1 << temiz.length; maske++) {
    let t = 0
    for (let i = 0; i < temiz.length; i++) if (maske & (1 << i)) t += temiz[i]
    toplamlar.add(Math.round(t * 10) / 10)
  }
  return [...toplamlar].sort((a, b) => a - b)
}

/**
 * Bir basamağın hangi bantlarla kurulduğu: 45 → "15 + 30".
 * Birden fazla kombinasyon aynı toplamı veriyorsa en az bant kullananı seçer.
 */
export function bantKombinasyonu(hedef: number, bandsKg: readonly number[]): number[] | null {
  const temiz = [...new Set(bandsKg.filter((x) => Number.isFinite(x) && x > 0))].sort((a, b) => b - a)
  let enIyi: number[] | null = null
  for (let maske = 1; maske < 1 << temiz.length; maske++) {
    const secim: number[] = []
    let t = 0
    for (let i = 0; i < temiz.length; i++) if (maske & (1 << i)) { t += temiz[i]; secim.push(temiz[i]) }
    if (Math.abs(t - hedef) > 0.05) continue
    if (!enIyi || secim.length < enIyi.length) enIyi = secim
  }
  return enIyi
}

/** "15 + 30 kg" gibi okunur etiket. Kombinasyon bulunamazsa düz sayı. */
export function bantEtiketi(hedef: number, bandsKg: readonly number[]): string {
  const k = bantKombinasyonu(hedef, bandsKg)
  if (!k || k.length <= 1) return `${n(hedef)} kg`
  return `${k.slice().sort((a, b) => a - b).map(n).join(' + ')} kg`
}

/** Verilen yükten sonraki basamak; en üstteysen null. */
export function sonrakiBasamak(mevcut: number | null, basamaklar: readonly number[]): number | null {
  if (basamaklar.length === 0) return null
  if (mevcut === null) return basamaklar[0]
  for (const b of basamaklar) if (b > mevcut + 0.05) return b
  return null
}

/** İki basamak arası artış oranı. 15 → 30 için 1 (yani %100). */
export function sicramaOrani(mevcut: number, sonraki: number): number {
  if (mevcut <= 0) return Infinity
  return (sonraki - mevcut) / mevcut
}

/**
 * Bir sonraki bandın "büyük sıçrama" sayıldığı eşik.
 *
 * 15'ten 30'a geçmek yükü İKİYE KATLAMAK. Dambılda 20→22.5 kg'lık bir adım
 * %12'dir ve 8 tekrara dönmek mantıklıdır; bantta aynı şeyi yapmak seti
 * baştan başarısız kılar. Büyük sıçramadan önce mevcut bantla tekrar
 * aralığını sonuna kadar kullanmak gerekiyor.
 */
export const BUYUK_SICRAMA = 0.3

/**
 * Bant büyütmeden önce çıkılabilecek tekrar tavanı.
 *
 * 20'den sonrası hipertrofi için verimsizleşiyor ve seans uzuyor; o noktada
 * artık tekrar değil direnç artmalı, sıçrama büyük olsa bile.
 */
export const BANT_TEKRAR_TAVANI = 20

export interface BantAdimi {
  /** 'load' → bandı büyüt, 'rep' → önce tekrarı artır. */
  tur: 'load' | 'rep'
  /** 'load' ise hedef direnç. */
  kg: number | null
  /** 'rep' ise yeni tekrar hedefi. */
  tekrar: number | null
  aciklama: string
}

/**
 * Bantta bir sonraki adım. Tekrar hedefi doldurulduğunda çağrılıyor.
 *
 * Kural: sıçrama küçükse (aynı bandın kombinasyonu gibi) doğrudan büyüt.
 * Büyükse önce tekrar tavanına kadar git, sonra büyüt ve tekrarı tabana çek.
 */
export function bantSonrakiAdim(o: {
  mevcutKg: number | null
  hedefTekrar: number
  tabanTekrar: number
  basamaklar: readonly number[]
  bandsKg: readonly number[]
}): BantAdimi {
  const { mevcutKg, hedefTekrar, tabanTekrar, basamaklar, bandsKg } = o
  const sonraki = sonrakiBasamak(mevcutKg, basamaklar)

  if (sonraki === null) {
    return {
      tur: 'rep', kg: null, tekrar: hedefTekrar + 2,
      aciklama: 'En ağır bant kombinasyonundasın. Sıradaki adım tekrar artırmak, '
        + 'tempoyu yavaşlatmak ya da tek kollu/tek bacaklı varyanta geçmek.',
    }
  }

  const sicrama = mevcutKg === null ? 0 : sicramaOrani(mevcutKg, sonraki)
  const yeniEtiket = bantEtiketi(sonraki, bandsKg)

  if (sicrama > BUYUK_SICRAMA && hedefTekrar < BANT_TEKRAR_TAVANI) {
    const yeni = Math.min(BANT_TEKRAR_TAVANI, hedefTekrar + 3)
    return {
      tur: 'rep', kg: mevcutKg, tekrar: yeni,
      aciklama: `Sıradaki direnç ${yeniEtiket} — yükü %${Math.round(sicrama * 100)} artırıyor, `
        + `tek adımda çok. Önce aynı bantla ${yeni} tekrara çık; oraya varınca büyüteceğiz.`,
    }
  }

  return {
    tur: 'load', kg: sonraki, tekrar: tabanTekrar,
    aciklama: `${yeniEtiket} bandına geç, tekrarı ${tabanTekrar}'e döndür.`,
  }
}

const n = (v: number) => (Math.round(v * 10) / 10).toLocaleString('tr-TR')
